import { NullAnalyticsService } from './null-analytics-service';
import type {
  AnalyticsEventOptions,
  AnalyticsManager,
  AnalyticsService,
  CreateAnalyticsManagerOptions,
} from './types';

export function createAnalyticsManager<T extends string, Config extends object>(
  options: CreateAnalyticsManagerOptions<T, Config>,
): AnalyticsManager {
  const activeServices = new Map<T, AnalyticsService>();

  const getActiveServices = (): AnalyticsService[] => {
    if (activeServices.size === 0) {
      console.debug(
        'No active analytics services. Using NullAnalyticsService.',
      );

      return [NullAnalyticsService];
    }

    return Array.from(activeServices.values());
  };

  const registerActiveServices = (
    options: CreateAnalyticsManagerOptions<T, Config>,
  ) => {
    Object.keys(options.providers).forEach((provider) => {
      const providerKey = provider as keyof typeof options.providers;
      const factory = options.providers[providerKey];

      if (!factory) {
        console.warn(
          `Analytics provider '${provider}' not registered. Skipping initialization.`,
        );

        return;
      }

      const service = factory();
      activeServices.set(provider as T, service);

      void service.initialize();
    });
  };

  registerActiveServices(options);

  return {
    addProvider: (provider: T, config: Config) => {
      const factory = options.providers[provider];

      if (!factory) {
        console.warn(
          `Analytics provider '${provider}' not registered. Skipping initialization.`,
        );

        return Promise.resolve();
      }

      const service = factory(config);
      activeServices.set(provider, service);

      return service.initialize();
    },

    removeProvider: (provider: T) => {
      activeServices.delete(provider);
    },

    identify: (userId: string, traits?: Record<string, string>) => {
      return Promise.all(
        getActiveServices().map((service) => service.identify(userId, traits)),
      );
    },

    group: (
      groupType: string,
      groupKey: string,
      groupProperties?: Record<string, string>,
    ) => {
      return Promise.all(
        getActiveServices().map((service) =>
          service.group(groupType, groupKey, groupProperties),
        ),
      );
    },

    registerProperties: (properties: Record<string, string>) => {
      return Promise.all(
        getActiveServices().map((service) =>
          service.registerProperties(properties),
        ),
      );
    },

    unregisterProperty: (key: string) => {
      return Promise.all(
        getActiveServices().map((service) => service.unregisterProperty(key)),
      );
    },

    trackPageView: (path: string) => {
      return Promise.all(
        getActiveServices().map((service) => service.trackPageView(path)),
      );
    },

    trackEvent: async (
      eventName: string,
      eventProperties?: Record<string, string | string[]>,
      options?: AnalyticsEventOptions,
    ) => {
      // Auto-identify user if user_id is present in event properties and an
      // explicit distinctId was not already supplied via options.
      if (
        !options?.distinctId &&
        eventProperties?.user_id &&
        typeof eventProperties.user_id === 'string'
      ) {
        await Promise.all(
          getActiveServices().map((service) =>
            service.identify(eventProperties.user_id as string),
          ),
        );
      }

      return Promise.all(
        getActiveServices().map((service) =>
          service.trackEvent(eventName, eventProperties, options),
        ),
      );
    },
  };
}
